import { CheckCircle2 } from "lucide-react";
import ScoreGauge from "@/components/ScoreGauge";
import EmailResultsCard from "@/components/checkup/EmailResultsCard";
import ShareResultsCard from "@/components/checkup/ShareResultsCard";
import ReviewCta from "@/components/checkup/ReviewCta";

const NEXT_STEPS = {
  estate_documents: "Learn the basics of wills and trusts, then note your questions for a qualified estate-planning attorney.",
  beneficiaries: "Set aside 15 minutes to review the beneficiary designations on your accounts and policies.",
  life_insurance: "Review your current coverage and consider whether it still matches your family's needs today.",
  healthcare_directives: "Learn about healthcare directives and think about who you would trust with medical decisions.",
  home_planning: "Review how your home is titled and consider what you would want to happen to it.",
  document_organization: "Gather your key documents into one secure place — and tell someone you trust where they are.",
  financial_poa: "Learn how a financial power of attorney works and who you might choose to serve.",
  final_expenses: "Consider how final expenses would be handled so your family isn't left guessing.",
};

export default function ScoreResults({ result, pathway, assessmentId }) {
  return (
    <>
      <div className="bg-[#0A1526] text-cream rounded-3xl p-7 sm:p-10 text-center hero-grain relative overflow-hidden border border-gold/30 shadow-[0_30px_70px_-24px_rgba(11,25,44,0.55)]">
        <div className="absolute inset-0 gold-glow" />
        <div className="relative">
          <p className="text-xs font-bold uppercase tracking-[0.28em] text-gold mb-5">{pathway.scoreLabel}</p>
          <ScoreGauge score={result.score} size={210} />
          <h1 className="mt-4 font-serif text-2xl sm:text-3xl font-semibold" data-testid="score-band-title">{result.band}</h1>
          <p className="mt-2 text-cream/75 text-sm max-w-md mx-auto leading-relaxed">{result.band_message}</p>
        </div>
      </div>

      {result.strongest_areas.length > 0 && (
        <section className="mt-8">
          <h2 className="font-serif text-2xl font-semibold text-navy">Your Strongest Areas</h2>
          <div className="mt-4 grid gap-2.5">
            {result.strongest_areas.map((s) => (
              <div key={s} className="flex items-center gap-3 bg-white rounded-xl border border-sand px-4 py-3">
                <CheckCircle2 className="w-5 h-5 text-[#15803D] shrink-0" />
                <span className="font-semibold text-navy text-sm sm:text-base">{s}</span>
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="mt-8">
        <h2 className="font-serif text-2xl font-semibold text-navy">Areas Worth Reviewing</h2>
        <div className="mt-4 space-y-3">
          {result.review_areas.map((area) => (
            <div key={area.key} className="bg-gold-light/60 rounded-2xl border border-sand p-5" data-testid="review-area-card">
              <p className="font-bold text-navy">{area.title}</p>
              <p className="text-sm text-slate-600 leading-relaxed mt-1">{area.description}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="mt-8">
        <h2 className="font-serif text-2xl font-semibold text-navy">Recommended Next Steps</h2>
        <div className="mt-4 space-y-3">
          {result.review_areas.map((area, i) => (
            <div key={area.key} className="bg-white rounded-2xl border border-sand p-5 flex gap-4">
              <span className="w-8 h-8 shrink-0 rounded-full bg-navy text-gold font-serif font-semibold flex items-center justify-center">{i + 1}</span>
              <p className="text-slate-700 leading-relaxed text-sm sm:text-base">{NEXT_STEPS[area.key]}</p>
            </div>
          ))}
        </div>
      </section>

      <div className="mt-8 bg-cream-dark rounded-2xl border border-sand p-5">
        <p className="text-xs text-slate-500 leading-relaxed">
          Legacy Wave Consulting provides educational information and insurance-related services. The
          Legacy Checkup™ is an educational assessment and does not provide legal, tax, financial, or
          individualized insurance advice. Results do not guarantee any legal or financial outcome.
          Insurance products and insurance-specific assistance are available only where Legacy Wave
          Consulting is appropriately licensed. Consult the appropriate licensed professional regarding
          your individual circumstances.
        </p>
      </div>

      <EmailResultsCard assessmentId={assessmentId} pathwayKey={pathway.key} />
      <ShareResultsCard score={result.score} pathway={pathway} />
      <ReviewCta pathway={pathway} />
    </>
  );
}
